import { useEffect, useState } from 'react';

/**
 * Lazy ticker that only re-renders when `active` is true.
 *
 * Returns the current epoch-second timestamp. When `active` flips to
 * true, an interval re-renders the consumer every `intervalMs` (default
 * 1000). When `active` is false, no timer is registered and the value
 * stays frozen — components binding the rate-limit countdown pay zero
 * cost while no bot is throttled.
 */
export function useNow(active: boolean, intervalMs: number = 1000): number {
  const [now, setNow] = useState<number>(() => Date.now() / 1000);
  useEffect(() => {
    if (!active) return;
    const id = setInterval(() => setNow(Date.now() / 1000), intervalMs);
    return () => clearInterval(id);
  }, [active, intervalMs]);
  return now;
}

/**
 * Compute the watchdog countdown (in seconds) from an AgentState row.
 *
 * Returns null when the bot isn't rate-limited or the backend hasn't
 * stamped a first-detect timestamp yet (stamped_at === 0 means the
 * watchdog cleared the marker post-nudge — the bot either recovered
 * or is about to re-stamp on the next tick).
 *
 * Shape lives in Cloud-API commit 31a2735.
 */
export function computeNudgeEta(
  stampedAt: number | undefined,
  retryAfter: number | undefined,
  nowSeconds: number
): number | null {
  if (!stampedAt || !retryAfter) return null;
  return Math.max(0, retryAfter - (nowSeconds - stampedAt));
}

export function formatMmSs(seconds: number): string {
  const total = Math.max(0, Math.round(seconds));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}
